import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Check, CheckCircle2, Circle, Copy, Download, LockOpen, LogIn, PauseCircle, Search } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusChip } from "@/pages/App/Coach/Books/components/bookStatus"
import { StageChip, formatDate } from "./components/bookStage"
import { DeleteBookDialog } from "@/components/book/DeleteBookDialog"

const LEADER = {
  not_sent: { label: "Leader: not sent", className: "text-zinc-500" },
  waiting: { label: "Leader: waiting", className: "text-amber-700" },
  approved: { label: "Leader: approved", className: "text-emerald-700" },
  skipped: { label: "Leader: skipped (test)", className: "text-violet-700" },
}

function Done({ done, children }) {
  return (
    <div className="flex items-center gap-2">
      {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-zinc-300" />}
      <span className={done ? "text-zinc-900" : "text-zinc-500"}>{children}</span>
    </div>
  )
}

// The existing "sign in as" link from People: a one-time login link for the
// coach, so admin can make a fix in the coach's own screens.
function SignInAsCoach({ coachId, coach }) {
  const [link, setLink] = useState(null)
  const [copied, setCopied] = useState(false)

  const makeLink = async () => {
    if (!window.confirm(`Make a one-time login link for ${coach}? Open it in a private window. Everything you do there changes ${coach}'s real account.`)) return
    const res = await fetch(`/app/admin/people/${coachId}/impersonate`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
    })
    const data = await res.json().catch(() => null)
    if (!res.ok) return window.alert(`The link was not made: ${data?.error || res.statusText}`)
    setLink(data.url)
  }

  return link ? (
    <div className="space-y-2">
      <div className="break-all rounded-md border bg-zinc-50 px-3 py-2 font-mono text-xs text-zinc-700">{link}</div>
      <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(link).then(() => setCopied(true))}>
        <Copy className="h-4 w-4" /> {copied ? "Copied" : "Copy the link"}
      </Button>
      <p className="text-xs text-zinc-500">The link works one time, for 15 minutes.</p>
    </div>
  ) : (
    <Button size="sm" variant="outline" onClick={makeLink}><LogIn className="h-4 w-4" /> Sign in as {coach}</Button>
  )
}

// One Kingdom Factors book for KF admin: its chapters and parts, its Brand
// check flags, and every assembly. Read only, except the flag decisions and
// Unlock for fix.
export default function BookShow({ currentUser, book, chapters = [], flags = [], assemblies = [] }) {
  const unlock = () => {
    if (window.confirm("Unlock this book for a fix? The coach can change it again, and it must be assembled and sent to print again. The published date stays the same.")) {
      router.post(`/app/admin/books/${book.id}/unlock`)
    }
  }
  const decide = (flag, action) => router.post(`/app/admin/brand_flags/${flag.id}/${action}`, {}, { preserveScroll: true })

  return (
    <AdminLayout currentUser={currentUser}>
      <Head title={`${book.title} - Kingdom Factor`} />
      <div className="space-y-6">
        <div>
          <Link href="/app/admin/books" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900">
            <ArrowLeft className="h-4 w-4" /> All books
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-zinc-900">{book.title}</h1>
            <StageChip stage={book.stage} />
          </div>
          <p className="text-zinc-600">
            By {book.coach} ({book.coachEmail}).
            {book.publishedAt && <> Published {formatDate(book.publishedAt)}.</>}
            {book.sentAt && book.sentAt !== book.publishedAt && <> Sent to print again {formatDate(book.sentAt)}.</>}
          </p>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-lg">Make a fix</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {book.stage === "sent" ? (
              <>
                <p className="text-zinc-600">The book is locked. Unlock it, then sign in as {book.coach} and make the fix in their screens. The book then needs a new assembly and Send to print.</p>
                <Button size="sm" onClick={unlock}><LockOpen className="h-4 w-4" /> Unlock for fix</Button>
              </>
            ) : (
              <p className="text-zinc-600">The book is not locked. To change it, sign in as {book.coach}.</p>
            )}
            <SignInAsCoach coachId={book.coachId} coach={book.coach} />
            <div className="border-t pt-3">
              <DeleteBookDialog title={book.title} path={`/app/admin/books/${book.id}`}
                note={book.publishedAt ? "It also leaves the Published in list, so it is not billed." : null} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Chapters ({book.chaptersApproved} of {book.chapters} approved)</CardTitle></CardHeader>
          <CardContent className="divide-y p-0">
            {chapters.length === 0 && <p className="px-6 pb-6 text-sm text-zinc-600">No chapters yet.</p>}
            {chapters.map((chapter) => (
              <div key={chapter.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-6 py-3 text-sm">
                <span className="w-5 text-zinc-400">{chapter.position}</span>
                <div className="min-w-48 flex-1">
                  <div className="font-medium text-zinc-900">{chapter.leaderName}{chapter.leaderCompany && <span className="font-normal text-zinc-500"> · {chapter.leaderCompany}</span>}</div>
                  {chapter.title && <div className="text-xs text-zinc-500">{chapter.title}</div>}
                </div>
                <StatusChip status={chapter.status} />
                <span className={`text-xs ${LEADER[chapter.leaderStatus].className}`}>{LEADER[chapter.leaderStatus].label}</span>
                {chapter.readable && (
                  <Link href={`/app/admin/books/${book.id}/chapters/${chapter.id}`} className="text-xs font-medium text-zinc-700 underline">Read</Link>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">The rest of the book</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Done done={book.letterApproved}>Author letter approved</Done>
            <Done done={book.aboutAuthorDone}>About the Author done</Done>
            <Done done={book.coverApproved}>Cover approved</Done>
            {book.stage !== "sent" && book.printBlockers.length > 0 && (
              <div className="pt-2">
                <div className="font-medium text-zinc-900">Before Send to print, the coach must:</div>
                <ul className="mt-1 list-disc pl-5 text-zinc-600">
                  {book.printBlockers.map((reason) => <li key={reason}>{reason}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Brand check flags</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm">
            {flags.length === 0 && <p className="text-zinc-600">No flags on the book as it is now.</p>}
            {flags.map((flag) => (
              <div key={flag.id} className="space-y-1 border-b pb-4 last:border-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-600">
                  {flag.severity === "serious"
                    ? <span className="rounded-full bg-red-600 px-2 py-0.5 font-semibold text-white">Serious</span>
                    : <span className="rounded-full bg-zinc-100 px-2 py-0.5 font-semibold text-zinc-700">Note</span>}
                  <span>{flag.topic}</span>
                  <span>·</span>
                  <span>{flag.part}</span>
                  <span className="ml-auto">
                    {flag.cleared ? "Cleared" : flag.held ? "On hold" : flag.kept ? "Kept by the coach" : "The coach has not decided"}
                  </span>
                </div>
                <p className="italic text-zinc-900">“{flag.quote}”</p>
                {flag.chapterId && (
                  <Link href={`/app/admin/books/${book.id}/chapters/${flag.chapterId}#flag-${flag.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-kf-red underline-offset-4 hover:underline">
                    <Search className="h-3.5 w-3.5" /> Read it in the chapter
                  </Link>
                )}
                <p className="text-zinc-700"><b>Why:</b> {flag.reason}</p>
                {flag.suggestion && <p className="text-zinc-700"><b>Suggested fix:</b> {flag.suggestion}</p>}
                {flag.severity === "serious" && !flag.cleared && (
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={() => decide(flag, "clear")}><Check className="h-4 w-4" /> Clear the flag</Button>
                    {!flag.held && (
                      <Button size="sm" variant="outline" onClick={() => decide(flag, "hold")}><PauseCircle className="h-4 w-4" /> Keep on hold</Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Assemblies</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {assemblies.length === 0 && <p className="text-zinc-600">The coach has not assembled the book yet.</p>}
            {assemblies.map((assembly) => (
              <div key={assembly.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b pb-2 last:border-0">
                <span className="font-medium text-zinc-900">Assembly {assembly.number}</span>
                <span className="text-zinc-500">{formatDate(assembly.createdAt)}</span>
                {assembly.pageCount && <span className="text-zinc-500">{assembly.pageCount} pages</span>}
                {assembly.sent && <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">Sent to print</span>}
                {assembly.current && <span className="text-xs text-blue-700">The book as it is now</span>}
                {assembly.building && <span className="text-xs text-zinc-500">Building</span>}
                {assembly.failed && <span className="text-xs text-red-700">Failed: {assembly.error}</span>}
                <span className="ml-auto flex gap-2">
                  {assembly.interiorUrl && <Button asChild size="sm" variant="outline"><a href={assembly.interiorUrl}><Download className="h-4 w-4" /> Interior</a></Button>}
                  {assembly.coverUrl && <Button asChild size="sm" variant="outline"><a href={assembly.coverUrl}><Download className="h-4 w-4" /> Cover</a></Button>}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}
